<template>
    <div class="flex flex-col gap-4 lg:flex-row lg:justify-between">
        <div :class="['min-w-20', selectableClass, '[--vs-dropdown-min-width:0px]']">
            <vx-selectable
                :size="props.size"
                :disabled="props.disabled"
                :options="repeatDelayConfig.options"
                v-model="repeatDelayValue"
                @update:modelValue="emitRepeatDelay()"
                :getOptionLabel="(option: number) => option"
                :reduce="(option: number) => option"
                :clearable="false">
            </vx-selectable>
        </div>
        <vx-tabs
            class="grow"
            :disabled="props.disabled"
            :size="props.size"
            v-model="repeatDelayUnit"
            @update:modelValue="onRepeatDelayUnitSelected()"
        >
            <vx-tab v-if="availableUnits.includes('minutes')" value="minutes">Min</vx-tab>
            <vx-tab v-if="availableUnits.includes('hours')" value="hours">Hr</vx-tab>
            <vx-tab v-if="availableUnits.includes('days')" value="days">D</vx-tab>
            <vx-tab v-if="availableUnits.includes('weeks')" value="weeks">Wk</vx-tab>
        </vx-tabs>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { VxSelectable, VxTab, VxTabs } from '@voxie/frontend-components';

const props = withDefaults(
    defineProps<{
        repeatDelay: number;
        disabled?: boolean;
        size?: 'sm' | 'md' | 'lg' | 'xl';
        selectableClass?: string;
        min?: number;
        minuteOptions?: number[];
    }>(),
    {
        disabled: false,
        size: 'lg',
        min: 0,
        minuteOptions: () => [15, 30, 45],
    }
);

const emit = defineEmits<{
    'update:repeatDelay': [value: number];
}>();

type RepeatDelayUnit = 'minutes' | 'hours' | 'days' | 'weeks';

const REPEAT_DELAY_UNITS: RepeatDelayUnit[] = ['minutes', 'hours', 'days', 'weeks'];

const repeatDelayUnit = ref<RepeatDelayUnit>('minutes');
const getRepeatDelayConfig = (unit: RepeatDelayUnit) => {
    if (unit === 'hours') {
        return {
            multiplier: 60,
            options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
        };
    } else if (unit === 'days') {
        return {
            multiplier: 60 * 24,
            options: [1, 2, 3, 4, 5, 6]
        };
    } else if (unit === 'weeks') {
        return {
            multiplier: 60 * 24 * 7,
            options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
        }
    } else {
        return {
            multiplier: 1,
            options: props.minuteOptions,
        };
    }
}

const optionsAtLeastMin = (unit: RepeatDelayUnit) => {
    const { multiplier, options } = getRepeatDelayConfig(unit);

    return options.filter((option) => option * multiplier >= props.min);
};

const availableUnits = computed(() => REPEAT_DELAY_UNITS.filter((unit) => optionsAtLeastMin(unit).length > 0));
const repeatDelayConfig = computed(() => ({
    multiplier: getRepeatDelayConfig(repeatDelayUnit.value).multiplier,
    options: optionsAtLeastMin(repeatDelayUnit.value),
}));
const repeatDelayValue = ref(repeatDelayConfig.value.options[0]);
const mounted = ref(false);

onMounted(async () => {
    processRepeatDelayInMinutes();

    await nextTick();
    mounted.value = true;
});

const emitRepeatDelay = () => {
    emit('update:repeatDelay', Number(repeatDelayValue.value) * Number(repeatDelayConfig.value.multiplier));
}

const processRepeatDelayInMinutes = () => {
    const repeatDelay = Number(props.repeatDelay);
    const smallestUnit = availableUnits.value[0];

    if (repeatDelay < props.min && undefined !== smallestUnit) {
        repeatDelayUnit.value = smallestUnit;
        repeatDelayValue.value = optionsAtLeastMin(smallestUnit)[0];
        emitRepeatDelay();

        return;
    }

    if (repeatDelay < 60) {
        repeatDelayUnit.value = 'minutes';
        repeatDelayValue.value = repeatDelay;
        return;
    }
    const unitsToCheck: RepeatDelayUnit[] = ['weeks', 'days', 'hours'];
    for (const unit of unitsToCheck) {
        const config = getRepeatDelayConfig(unit);

        const multiplier = config.multiplier;
        if (repeatDelay % multiplier === 0) {
            repeatDelayUnit.value = unit;
            repeatDelayValue.value = repeatDelay / multiplier;
            return;
        }
    }

    repeatDelayUnit.value = 'minutes';
    repeatDelayValue.value = repeatDelay;
}

const onRepeatDelayUnitSelected = () => {
    if (!mounted.value) {
        return;
    }

    repeatDelayValue.value = repeatDelayConfig.value.options[0];
    emitRepeatDelay();
};

watch(() => props.repeatDelay, () => {
    processRepeatDelayInMinutes();
});
</script>
